import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useImageNaturalSize,
} from "../../../lib/toolPrepImageLayout";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../shared/Popup/AiFeaturePreview.css";
import "../../../shared/Popup/ToolPrepStage.css";
import "./AiShadowLivePreview.css";

const AiShadowLivePreview = ({
  imageUrl = null,
  loading = false,
  loadingLabel = "Working…",
  emptyLabel = "Upload an image to preview",
  transparent = false,
  whiteBackground = false,
  showLiveShadow = false,
  liveCast = null,
  showSubjectBox = false,
}) => {
  const rootRef = useRef(null);
  const naturalSize = useImageNaturalSize(imageUrl);
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });

  const fitFrame = useMemo(() => {
    if (!imageUrl || !(naturalSize.width > 0) || !(naturalSize.height > 0)) {
      return null;
    }
    const boxW = stageSize.width;
    const boxH = stageSize.height;
    if (!(boxW > 0) || !(boxH > 0)) return null;
    const scale = Math.min(boxW / naturalSize.width, boxH / naturalSize.height);
    const width = naturalSize.width * scale;
    const height = naturalSize.height * scale;
    return {
      x: (boxW - width) / 2,
      y: (boxH - height) / 2,
      width,
      height,
    };
  }, [
    imageUrl,
    naturalSize.height,
    naturalSize.width,
    stageSize.height,
    stageSize.width,
  ]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !loading;
  const [isCropping, setIsCropping] = useState(false);
  const { object, setObject, wrapperStyle, imageStyle, crop, cropExtent, originalImageSize } = useToolPrepSubjectObject({
    enabled: interactive,
    imageUrl,
    fitFrame,
    resetKey: imageUrl,
  });

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;

    const measure = () => {
      const aspect =
        imageUrl && naturalSize.width > 0 && naturalSize.height > 0
          ? naturalSize.width / naturalSize.height
          : 1;
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        aspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };

    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [imageUrl, loading, naturalSize.width, naturalSize.height]);

  const cast = showLiveShadow && liveCast ? liveCast : null;
  const scale = Math.min(stageSize.width, stageSize.height) / 320;
  const blur = cast ? Math.round(cast.blur * Math.max(0.85, scale)) : 0;

  return (
    <ToolPrepCanvasStage
      className="aftp-preview aslp-preview"
      ref={rootRef}
      metaImageUrl={!loading ? imageUrl : null}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      {loading ? (
        <div className="aftp-preview__loader">
          <RoundLoader />
          <p>{loadingLabel}</p>
        </div>
      ) : null}
      {!loading && !imageUrl ? (
        <div
          className="aftp-preview__empty-stage"
          style={{ width: stageSize.width, height: stageSize.height }}
          aria-label="Empty preview"
        >
          <p className="aftp-preview__empty">{emptyLabel}</p>
        </div>
      ) : null}
      {!loading && imageUrl ? (
        <div
          className={`aftp-preview__frame aslp-frame${
            transparent ? " aftp-preview__frame--transparent" : ""
          }${whiteBackground ? " is-white" : ""}${
            showSubjectBox ? " aftp-preview__frame--subject-bb" : ""
          }`}
          style={{
            width: stageSize.width,
            height: stageSize.height,
            position: "relative",
          }}
        >
          <div className="tool-prep-stage__clip">
            {cast ? (
              <div
                className={`aslp-cast${cast.contact ? " is-contact" : ""}`}
                aria-hidden="true"
                style={{
                  opacity: cast.opacity,
                  filter: `blur(${blur}px)`,
                  transform: cast.contact
                    ? `translate(-50%, -30%) rotate(${cast.angleDeg}deg) scale(${cast.scaleX}, ${cast.scaleY})`
                    : `translateY(-50%) rotate(${cast.angleDeg}deg) scale(${cast.scaleX}, ${cast.scaleY})`,
                }}
              />
            ) : null}
            {interactive && object && wrapperStyle ? (
              <div
                className="aftp-preview__subject"
                style={subjectWrapperStyleForCrop(
                  { ...wrapperStyle, overflow: "visible" },
                  isCropping
                )}
              >
                <ToolPrepSubjectCropGhost
                  active={isCropping}
                  src={imageUrl}
                  style={imageStyle}
                />
                <img
                  className="aslp-subject aftp-preview__img--absolute"
                  src={imageUrl}
                  alt="Tool preview"
                  draggable={false}
                  style={subjectMainImageStyleForCrop(imageStyle, {
                    isCropping,
                    crop,
                    originalImageSize,
                    object,
                  })}
                />
              </div>
            ) : (
              <img
                className="aslp-subject"
                src={imageUrl}
                alt="Tool preview"
                draggable={false}
              />
            )}
          </div>
          {interactive && object && wrapperStyle ? (
            <div className="tool-prep-stage__chrome">
              <ToolPrepSubjectBoundingBox
                enabled
                interactive
                frame={object}
                crop={crop}
                cropExtent={cropExtent}
                originalImageSize={originalImageSize}
                onChange={setObject}
                onCroppingChange={setIsCropping}
                stageSize={stageSize}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </ToolPrepCanvasStage>
  );
};

export default AiShadowLivePreview;
